import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Download,
  Info,
  Loader2,
  Wallet,
  Settings as SettingsIcon,
  FileSpreadsheet,
  UploadCloud,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Eye,
  Send,
  ClipboardCheck,
  CircleSlash,
} from "lucide-react";

function istToday() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
}

function ymOptions() {
  const out = [];
  const now = istToday();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value: ym, label: d.toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
}

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const COUNT_TILE_TONES = {
  slate:   "border-slate-200   bg-slate-50    text-slate-900",
  amber:   "border-amber-200   bg-amber-50    text-amber-900",
  emerald: "border-emerald-200 bg-emerald-50  text-emerald-900",
  rose:    "border-rose-200    bg-rose-50     text-rose-900",
};

function CountTile({ label, value, tone = "slate", testId }) {
  const cls = COUNT_TILE_TONES[tone] || COUNT_TILE_TONES.slate;
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function ArtifactSlot({ month, kind, existing, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const niceKind = kind === "attendance" ? "Contractor Attendance" : "Wage Register";

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("month", month);
      fd.append("kind", kind);
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/admin/salary-report/artifacts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
        body: fd,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      toast.success(`${niceKind} uploaded`);
      onChanged?.();
    } catch (err) {
      toast.error(`Upload failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!existing) return;
    if (!window.confirm(`Remove ${niceKind} for this month?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/salary-report/artifacts/${existing.id}`);
      toast.success(`${niceKind} removed`);
      onChanged?.();
    } catch {
      toast.error("Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const onPreview = () => {
    if (!existing) return;
    const url = `${API_BASE}/admin/salary-report/artifacts/${existing.id}/download?inline=1`;
    // For .xlsx browsers will download regardless; we use the same endpoint.
    window.open(url, "_blank", "noopener");
  };

  const onDownloadTemplate = () => {
    const url = `${API_BASE}/admin/salary-report/template?kind=${kind}&month=${month}`;
    const a = document.createElement("a");
    a.href = url + `&_=${Date.now()}`;
    // Append auth header via fetch + blob (template needs admin auth)
    fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        const dl = document.createElement("a");
        dl.href = URL.createObjectURL(blob);
        dl.download = `${kind}_template_${month}.xlsx`;
        dl.click();
        URL.revokeObjectURL(dl.href);
      })
      .catch(() => toast.error("Failed to download template"));
  };

  return (
    <Card data-testid={`sr-artifact-${kind}`} className={existing ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200"}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-[14px]">
          <span className="flex items-center gap-2">
            {existing ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <UploadCloud className="h-4 w-4 text-slate-500" />}
            {niceKind}
          </span>
          <button
            type="button"
            onClick={onDownloadTemplate}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
            data-testid={`sr-artifact-${kind}-template`}
            title="Download a sample Excel matching the expected column layout"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Template
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {existing ? (
          <>
            <div className="text-[13px] text-slate-700">
              <div className="font-medium break-all">{existing.file_name}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <Badge variant="outline" className="bg-white">{existing.parsed_row_count} rows</Badge>
                <span>{(existing.file_size / 1024).toFixed(0)} KB</span>
                <span>uploaded by {existing.uploaded_by_name || "—"}</span>
              </div>
              {existing.parsed_warning && (
                <div className="mt-1 text-[11px] text-amber-700 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 mt-0.5" /> {existing.parsed_warning}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onPreview} data-testid={`sr-artifact-${kind}-download`}>
                <Eye className="mr-1.5 h-3.5 w-3.5" /> Download
              </Button>
              <Button size="sm" variant="outline" onClick={onPick} disabled={busy} data-testid={`sr-artifact-${kind}-replace`}>
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Replace
              </Button>
              <Button size="sm" variant="outline" onClick={onDelete} disabled={busy} className="ml-auto text-rose-700 hover:bg-rose-50" data-testid={`sr-artifact-${kind}-delete`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <div className="text-[12px] text-slate-500">
              No file uploaded for this month.
              <button
                type="button"
                onClick={onDownloadTemplate}
                className="ml-1 text-indigo-700 hover:underline font-medium"
                data-testid={`sr-artifact-${kind}-template-inline`}
              >
                Download the sample template
              </button>{" "}
              to see the expected columns.
            </div>
            <Button size="sm" onClick={onPick} disabled={busy} data-testid={`sr-artifact-${kind}-upload`}>
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="mr-1.5 h-3.5 w-3.5" />}
              Upload .xlsx
            </Button>
          </div>
        )}
        <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} />
      </CardContent>
    </Card>
  );
}

export default function SalaryReportPage() {
  const [rates, setRates] = useState([]);
  const [month, setMonth] = useState(() => {
    const d = istToday();
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
  });
  const [artifacts, setArtifacts] = useState({ attendance: null, wage_register: null });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verLoading, setVerLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const months = useMemo(() => ymOptions(), []);
  const monthLabel = months.find((m) => m.value === month)?.label || month;

  const reload = async () => {
    try {
      const res = await api.get(`/admin/salary-report/artifacts?month=${month}`).then((r) => r.data);
      const out = { attendance: null, wage_register: null };
      for (const a of res.artifacts || []) {
        out[a.kind] = a;
      }
      setArtifacts(out);
    } catch {
      toast.error("Failed to load artifacts");
    }
    await loadPreview();
    await loadVerification();
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await api.get(`/admin/salary-report/preview?month=${month}`).then((r) => r.data);
      setPreview(res);
    } catch {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadVerification = async () => {
    setVerLoading(true);
    try {
      const res = await api.get(`/admin/salary-report/verifications?month=${month}`).then((r) => r.data);
      setVerification(res);
    } catch {
      setVerification(null);
    } finally {
      setVerLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/payroll-rates").then((x) => x.data);
        setRates(r);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  async function downloadXlsx() {
    setBusy(true);
    try {
      const url = `${API_BASE}/admin/salary-report/generate?month=${month}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `salary_report_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Salary workbook downloaded");
    } catch {
      toast.error("Failed to generate salary report");
    } finally {
      setBusy(false);
    }
  }

  async function publishVerification() {
    if (!window.confirm(
      `Publish ${monthLabel} salary to PMC supervisors for paid-amount verification?\n\n` +
      `Each park's supervisor will be able to Confirm or Dispute every employee's net pay.\n` +
      `Already-decided rows will be preserved.`,
    )) return;
    setPublishing(true);
    try {
      const res = await api.post(`/admin/salary-report/publish-verification?month=${month}`).then((r) => r.data);
      toast.success(
        `Published. New: ${res.inserted}, refreshed pending: ${res.updated_pending}, decided kept: ${res.refreshed_decided}.`,
      );
      await loadVerification();
    } catch (err) {
      toast.error(`Publish failed: ${err?.response?.data?.detail || err.message || err}`);
    } finally {
      setPublishing(false);
    }
  }

  async function exportVerification() {
    setExporting(true);
    try {
      const url = `${API_BASE}/admin/salary-report/verifications/export?month=${month}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `salary_verification_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Verification report downloaded");
    } catch {
      toast.error("Failed to export verification report");
    } finally {
      setExporting(false);
    }
  }

  const tot = preview?.totals || {};
  const rec = preview?.reconciliation || {};
  const counts = [
    { key: "over", label: "Over-claimed", count: rec.over_claim_count || 0, list: rec.over_claim_top || [], icon: AlertTriangle, color: "amber" },
    { key: "under", label: "Under-claimed", count: rec.under_claim_count || 0, list: rec.under_claim_top || [], icon: AlertTriangle, color: "sky" },
    { key: "missing", label: "In contractor file, not in our DB", count: rec.missing_in_our_count || 0, list: rec.missing_in_our_top || [], icon: XCircle, color: "rose" },
  ];

  return (
    <AppShell
      title="Salary Report"
      subtitle="Upload contractor's monthly files, reconcile against our attendance, and download the final salary workbook"
    >
      <div className="space-y-5">
        <Alert className="border-emerald-200 bg-emerald-50/60" data-testid="sr-info-alert">
          <Info className="h-4 w-4 text-emerald-700" />
          <AlertTitle className="text-emerald-900">How this works</AlertTitle>
          <AlertDescription className="text-emerald-900/90 text-[13px] leading-relaxed">
            <strong>Our captured attendance is the final source of truth.</strong> Upload the two
            contractor files for the month — we cross-check claimed days vs our days and apply a
            <strong> per-employee cap</strong>: <em>payable_days = min(our_days, contractor_claimed)</em>.
            Relievers are paid normally. Salary components come from&nbsp;
            <Link to="/admin/payroll-rates" className="font-medium underline underline-offset-2 hover:text-emerald-700">
              Settings &rarr; Payroll Rates
            </Link>.
          </AlertDescription>
        </Alert>

        {/* Month selector */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Salary month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[220px]" data-testid="sr-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Link to="/admin/payroll-rates" className="ml-auto">
            <Button variant="outline" size="sm" data-testid="sr-edit-rates-btn">
              <SettingsIcon className="mr-1.5 h-4 w-4" /> Edit Payroll Rates
            </Button>
          </Link>
        </div>

        {/* Step 1 — Upload artifacts */}
        <Card data-testid="sr-upload-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <UploadCloud className="h-4 w-4 text-sky-700" />
              Step 1 &mdash; Upload contractor files for {monthLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <ArtifactSlot month={month} kind="attendance" existing={artifacts.attendance} onChanged={reload} />
            <ArtifactSlot month={month} kind="wage_register" existing={artifacts.wage_register} onChanged={reload} />
          </CardContent>
        </Card>

        {/* Step 2 — Reconciliation */}
        <Card data-testid="sr-reconcile-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <FileSpreadsheet className="h-4 w-4 text-amber-700" />
              Step 2 &mdash; Reconciliation summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingPreview ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Calculating&hellip;
              </div>
            ) : !preview ? (
              <div className="text-sm text-muted-foreground">Upload at least one file to see the reconciliation summary.</div>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-3" data-testid="sr-totals">
                  <div className="rounded-lg border bg-white p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Our employees</div>
                    <div className="text-2xl font-semibold text-slate-900">{tot.employees_ours ?? 0}</div>
                    <div className="text-[11px] text-slate-500">incl. relievers, paid</div>
                  </div>
                  <div className="rounded-lg border bg-white p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Wage register</div>
                    <div className="text-2xl font-semibold text-slate-900">{tot.employees_wage_register ?? 0}</div>
                    <div className="text-[11px] text-slate-500">contractor's claim</div>
                  </div>
                  <div className="rounded-lg border bg-white p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Matched (by code)</div>
                    <div className="text-2xl font-semibold text-emerald-700">{tot.matched ?? 0}</div>
                    <div className="text-[11px] text-slate-500">days will be capped per employee</div>
                  </div>
                </div>

                <Tabs defaultValue={counts[0].key}>
                  <TabsList data-testid="sr-recon-tabs">
                    {counts.map((c) => (
                      <TabsTrigger key={c.key} value={c.key} data-testid={`sr-recon-tab-${c.key}`}>
                        {c.label}
                        <Badge variant="outline" className={`ml-2 ${c.count > 0 ? "border-amber-300 bg-amber-50 text-amber-800" : ""}`}>
                          {c.count}
                        </Badge>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {counts.map((c) => (
                    <TabsContent key={c.key} value={c.key} className="mt-3">
                      {c.list.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No discrepancies in this bucket.</div>
                      ) : (
                        <div className="overflow-x-auto rounded border">
                          <table className="w-full text-[13px]" data-testid={`sr-recon-table-${c.key}`}>
                            <thead>
                              <tr className="border-b bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                                <th className="py-2 pl-3 pr-3">Code</th>
                                <th className="py-2 pr-3">Name</th>
                                {c.key === "missing" ? (
                                  <>
                                    <th className="py-2 pr-3">Location (contractor)</th>
                                    <th className="py-2 pr-3 text-right">Claimed days</th>
                                  </>
                                ) : (
                                  <>
                                    <th className="py-2 pr-3 text-right">Our days</th>
                                    <th className="py-2 pr-3 text-right">Claimed</th>
                                    <th className="py-2 pr-3 text-right">Diff</th>
                                    <th className="py-2 pr-3 text-right">Payable</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {c.list.slice(0, 25).map((row, i) => (
                                <tr key={i} className="border-b last:border-b-0">
                                  <td className="py-1.5 pl-3 pr-3 font-mono">{row.code}</td>
                                  <td className="py-1.5 pr-3">{row.name}</td>
                                  {c.key === "missing" ? (
                                    <>
                                      <td className="py-1.5 pr-3">{row.location_raw || "—"}</td>
                                      <td className="py-1.5 pr-3 text-right tabular-nums">{row.days_working ?? 0}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="py-1.5 pr-3 text-right tabular-nums">{row.our}</td>
                                      <td className="py-1.5 pr-3 text-right tabular-nums">{row.claimed}</td>
                                      <td className={`py-1.5 pr-3 text-right tabular-nums font-medium ${row.claimed > row.our ? "text-amber-700" : "text-sky-700"}`}>
                                        {row.claimed - row.our > 0 ? "+" : ""}{row.claimed - row.our}
                                      </td>
                                      <td className="py-1.5 pr-3 text-right tabular-nums">{Math.min(row.our, row.claimed)}</td>
                                    </>
                                  )}
                                </tr>
                              ))}
                              {c.list.length > 25 && (
                                <tr><td colSpan={6} className="py-2 px-3 text-center text-[12px] text-muted-foreground">
                                  Showing first 25 of {c.list.length}. Full details in the downloaded workbook.
                                </td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </TabsContent>
                  ))}
                </Tabs>

                {preview.park_rows?.length > 0 && (
                  <div>
                    <div className="mb-2 mt-4 text-[13px] font-medium text-slate-700">Park-wise mandays</div>
                    <div className="overflow-x-auto rounded border">
                      <table className="w-full text-[12px]" data-testid="sr-park-table">
                        <thead>
                          <tr className="border-b bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                            <th className="py-1.5 pl-2 pr-2">Park</th>
                            <th className="py-1.5 pr-2 text-right">Approved (S/D/Sup)</th>
                            <th className="py-1.5 pr-2 text-right">Deployed (S/D/Sup)</th>
                            <th className="py-1.5 pr-2 text-right">Mandays ours</th>
                            <th className="py-1.5 pr-2 text-right">Mandays claimed</th>
                            <th className="py-1.5 pr-2 text-right">Payable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.park_rows.map((p) => {
                            const sum = (d) => (d.Sweeper || 0) + (d.Driver || 0) + (d.Supervisor || 0);
                            const overshoot = sum(p.mandays_claimed) > sum(p.mandays_payable);
                            return (
                              <tr key={p.park_id} className="border-b last:border-b-0">
                                <td className="py-1.5 pl-2 pr-2">{p.park_name}</td>
                                <td className="py-1.5 pr-2 text-right tabular-nums">{p.approved.Sweeper}/{p.approved.Driver}/{p.approved.Supervisor}</td>
                                <td className="py-1.5 pr-2 text-right tabular-nums">{p.deployed.Sweeper}/{p.deployed.Driver}/{p.deployed.Supervisor}</td>
                                <td className="py-1.5 pr-2 text-right tabular-nums">{sum(p.mandays_ours)}</td>
                                <td className={`py-1.5 pr-2 text-right tabular-nums ${overshoot ? "text-amber-700 font-medium" : ""}`}>
                                  {sum(p.mandays_claimed)}
                                </td>
                                <td className="py-1.5 pr-2 text-right tabular-nums font-semibold">{sum(p.mandays_payable)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Step 3 — Download */}
        <Card data-testid="sr-generate-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <FileSpreadsheet className="h-4 w-4 text-indigo-700" />
              Step 3 &mdash; Download the salary workbook
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-700">
              <div>Generates: <span className="font-medium">Summary, Detail, Attendance pivot, and one sheet per park.</span></div>
              <div className="mt-1 text-[12px] text-slate-500">Rows where contractor claimed more days than our records are highlighted in amber.</div>
            </div>
            <Button onClick={downloadXlsx} disabled={busy} data-testid="sr-download-btn" className="min-w-[200px]">
              {busy ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating&hellip;</>
              ) : (
                <><Download className="mr-2 h-4 w-4" /> Download Salary XLSX</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Step 4 — Publish for supervisor verification */}
        <Card data-testid="sr-publish-card" className="border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <Send className="h-4 w-4 text-emerald-700" />
              Step 4 &mdash; Publish for PMC supervisor verification
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-700">
              <div>Snapshots each employee&apos;s system-computed net pay and sends them to the assigned PMC supervisor.</div>
              <div className="mt-1 text-[12px] text-slate-500">
                Supervisors can <strong>Confirm</strong> the amount as paid, or <strong>Dispute</strong> with the actual amount and a reason. Re-publishing keeps decided rows.
              </div>
              {verification?.last_published_at && (
                <div className="mt-1 text-[11px] text-emerald-700" data-testid="sr-last-published">
                  Last published: {new Date(verification.last_published_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST
                </div>
              )}
            </div>
            <Button
              onClick={publishVerification}
              disabled={publishing || !preview}
              className="min-w-[220px] bg-emerald-600 hover:bg-emerald-700"
              data-testid="sr-publish-btn"
            >
              {publishing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Publishing&hellip;</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> Publish for Verification</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Verification status */}
        <Card data-testid="sr-verification-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <ClipboardCheck className="h-4 w-4 text-violet-700" />
              Verification status &mdash; {monthLabel}
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={exportVerification}
              disabled={exporting || !verification?.counts?.total}
              data-testid="sr-verification-export"
            >
              {exporting ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Exporting</>
              ) : (
                <><Download className="mr-1.5 h-3.5 w-3.5" /> Export XLSX</>
              )}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {verLoading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading verification status&hellip;
              </div>
            ) : !verification || (verification.counts?.total ?? 0) === 0 ? (
              <div className="rounded-md border border-dashed bg-slate-50 px-4 py-6 text-center text-sm text-muted-foreground" data-testid="sr-verification-empty">
                <CircleSlash className="mx-auto mb-2 h-5 w-5 text-slate-400" />
                Not published yet for this month. Click <strong>Publish for Verification</strong> in Step 4 above to send rows to supervisors.
              </div>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-4" data-testid="sr-verification-counts">
                  <CountTile label="Total rows" value={verification.counts.total} tone="slate" testId="sr-ver-total" />
                  <CountTile label="Pending" value={verification.counts.pending} tone="amber" testId="sr-ver-pending" />
                  <CountTile label="Confirmed" value={verification.counts.confirmed} tone="emerald" testId="sr-ver-confirmed" />
                  <CountTile label="Disputed" value={verification.counts.disputed} tone="rose" testId="sr-ver-disputed" />
                </div>

                {verification.park_rows?.length > 0 && (
                  <div className="overflow-x-auto rounded border">
                    <table className="w-full text-[12px]" data-testid="sr-verification-park-table">
                      <thead>
                        <tr className="border-b bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                          <th className="py-1.5 pl-2 pr-2">Park</th>
                          <th className="py-1.5 pr-2 text-right">Pending</th>
                          <th className="py-1.5 pr-2 text-right">Confirmed</th>
                          <th className="py-1.5 pr-2 text-right">Disputed</th>
                          <th className="py-1.5 pr-2 text-right">Total</th>
                          <th className="py-1.5 pr-2 text-right">System ₹</th>
                          <th className="py-1.5 pr-2 text-right">Paid ₹</th>
                          <th className="py-1.5 pr-2 text-right">Diff ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verification.park_rows.map((p) => {
                          const diff = (p.paid_total || 0) - (p.system_total || 0);
                          return (
                            <tr key={p.park_id} className="border-b last:border-b-0">
                              <td className="py-1.5 pl-2 pr-2">
                                <span className="font-mono text-[11px] text-slate-500 mr-1.5">{p.park_code}</span>
                                {p.park_name}
                              </td>
                              <td className="py-1.5 pr-2 text-right tabular-nums text-amber-700">{p.pending}</td>
                              <td className="py-1.5 pr-2 text-right tabular-nums text-emerald-700">{p.confirmed}</td>
                              <td className="py-1.5 pr-2 text-right tabular-nums text-rose-700">{p.disputed}</td>
                              <td className="py-1.5 pr-2 text-right tabular-nums font-medium">{p.total}</td>
                              <td className="py-1.5 pr-2 text-right tabular-nums">{fmtINR(p.system_total)}</td>
                              <td className="py-1.5 pr-2 text-right tabular-nums">{p.paid_total > 0 ? fmtINR(p.paid_total) : "—"}</td>
                              <td className={`py-1.5 pr-2 text-right tabular-nums font-medium ${diff === 0 ? "text-slate-500" : diff < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                {p.paid_total > 0 ? (diff > 0 ? "+" : "") + fmtINR(diff) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {verification.counts.disputed > 0 && (
                  <div data-testid="sr-disputed-list">
                    <div className="mb-2 mt-3 flex items-center gap-2 text-[13px] font-medium text-rose-800">
                      <AlertTriangle className="h-3.5 w-3.5" /> Disputed rows
                    </div>
                    <div className="overflow-x-auto rounded border">
                      <table className="w-full text-[12px]">
                        <thead>
                          <tr className="border-b bg-rose-50/60 text-left text-[10px] uppercase tracking-wide text-rose-700">
                            <th className="py-1.5 pl-2 pr-2">Park</th>
                            <th className="py-1.5 pr-2">Code</th>
                            <th className="py-1.5 pr-2">Name</th>
                            <th className="py-1.5 pr-2 text-right">System ₹</th>
                            <th className="py-1.5 pr-2 text-right">Paid ₹</th>
                            <th className="py-1.5 pr-2 text-right">Diff ₹</th>
                            <th className="py-1.5 pr-2">Notes</th>
                            <th className="py-1.5 pr-2">Verified by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {verification.rows.filter((r) => r.status === "disputed").map((r) => {
                            const d = (r.actual_paid_amount || 0) - (r.system_net_pay || 0);
                            return (
                              <tr key={r.id} className="border-b last:border-b-0">
                                <td className="py-1.5 pl-2 pr-2">{r.park_code}</td>
                                <td className="py-1.5 pr-2 font-mono">{r.employee_code}</td>
                                <td className="py-1.5 pr-2">{r.employee_name}</td>
                                <td className="py-1.5 pr-2 text-right tabular-nums">{fmtINR(r.system_net_pay)}</td>
                                <td className="py-1.5 pr-2 text-right tabular-nums">{fmtINR(r.actual_paid_amount || 0)}</td>
                                <td className={`py-1.5 pr-2 text-right tabular-nums font-medium ${d < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                  {d > 0 ? "+" : ""}{fmtINR(d)}
                                </td>
                                <td className="py-1.5 pr-2 max-w-[260px] truncate" title={r.notes || ""}>{r.notes || ""}</td>
                                <td className="py-1.5 pr-2">{r.verified_by_name || ""}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Reference — active payroll rates */}
        <Card data-testid="sr-rates-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[15px]">
              <Wallet className="h-4 w-4 text-amber-700" />
              Active payroll rates (per worker · monthly)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rates.length === 0 ? (
              <div className="text-sm text-muted-foreground">No payroll rates configured.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Designation</th>
                      <th className="py-2 pr-3 text-right">BASIC+DA</th>
                      <th className="py-2 pr-3 text-right">HRA</th>
                      <th className="py-2 pr-3 text-right">Other</th>
                      <th className="py-2 pr-3 text-right">Gross</th>
                      <th className="py-2 pr-3 text-right">PF %</th>
                      <th className="py-2 pr-3 text-right">PF Cap</th>
                      <th className="py-2 pr-3 text-right">ESI %</th>
                      <th className="py-2 pr-3 text-right">PT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((r) => {
                      const gross = (r.basic_da || 0) + (r.hra || 0) + (r.other_allowances || 0);
                      return (
                        <tr key={r.designation} className="border-b last:border-b-0" data-testid={`sr-rate-row-${r.designation}`}>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className="font-medium">{r.designation}</Badge>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">{fmtINR(r.basic_da)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{fmtINR(r.hra)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{fmtINR(r.other_allowances)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums font-semibold">{fmtINR(gross)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{Number(r.pf_percent || 0).toFixed(2)}%</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{fmtINR(r.pf_wage_cap)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{Number(r.esi_percent || 0).toFixed(2)}%</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{fmtINR(r.pt_amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
